import { useCallback, useEffect, useState, useRef } from 'react';

import { cloneDeep } from 'lodash';

/**
 * React hook reminiscent of React class setState. Multi-key partial updates,
 * post-update callback, and a reset function. Carried over from the
 * appstream-cloud / gmetrix-labs codebases for consistency.
 *
 * The setter and resetter are memoized with useCallback so consumers can
 * safely include them in useEffect dep arrays without triggering re-runs
 * on every render. A stateRef keeps the closure pointed at the latest
 * state without rebuilding the setter — without this, listing the setter
 * in a dep array would cause infinite re-fetch loops on data-loading
 * effects (which is exactly the bug this hook used to spawn).
 *
 * @param {*} initialValue
 * @returns [state, setState(patch, callback?), resetState(callback?)]
 */
const useSuperState = initialValue => {

    const [state, setState] = useState(initialValue);

    const initialRef = useRef();
    const callbacksRef = useRef([]);
    const stateRef = useRef(state);

    useEffect(() => {

        stateRef.current = state;

    }, [state]);

    useEffect(() => {

        initialRef.current = cloneDeep(initialValue);

    }, [initialValue]);

    useEffect(() => {

        callbacksRef.current.forEach(callback => callback(state));
        callbacksRef.current = [];

    }, [state]);

    const setSuperState = useCallback((newState, callback) => {

        if (typeof newState === 'function')
            newState = newState(stateRef.current);

        // Preserve the legacy in-place mutation: some callers read
        // stateRef.current synchronously after setSuperState and expect
        // the patch to be visible without waiting for a re-render.
        const current = stateRef.current;
        if (current && typeof current === 'object')
            Object.keys(newState).forEach(key => {

                current[key] = newState[key];

            });

        if (typeof callback === 'function')
            callbacksRef.current.push(callback);

        setState(prevState => ({ ...prevState, ...newState }));

    }, []);

    const resetSuperState = useCallback(callback => {

        if (typeof callback === 'function')
            callbacksRef.current.push(callback);

        setState(initialRef.current);

    }, []);

    if (typeof initialValue !== 'object' || Array.isArray(initialValue) || !initialValue)
        return [state, setState];

    return [state, setSuperState, resetSuperState];

};

export default useSuperState;
